import { BatchLoadDrop, BatchLoadExperimentsData, Entry } from 'business/models/Models';
import { GetNormalizedBatchloadEntryValue } from 'utils/Global';
import BatchloadExperimentReviewExpOptions from 'enums/BatchloadExperimentReviewExpOptions';
import { getHyphenedName } from '../Hooks/ExperimentCommands/utils';
import { cloneDeep } from 'lodash';

export const ESampleValidationStatusCode = {
  VALID: 0,
  DUPLICATE: 1,
} as const;

// HACK: Modal is fixed width, and FDS table needs defined width (not full or 100%)
export const FixedTableWidth = '1094px';

const BATCHLOAD_DROP_FIELDS: (keyof BatchLoadDrop)[] = [
  'protein',
  'proteinConcentration',
  'proteinMolecularWeight',
  'proteinLot',
  'ligand',
  'ligandConcentration',
  'ligandMolecularWeight',
  'ligandLot',
  'compound',
  'compoundConcentration',
  'compoundMolecularWeight',
  'compoundLot',
];

export function isSameDropIngredients(
  drop1: BatchLoadDrop,
  drop2: BatchLoadDrop,
): boolean {
  return BATCHLOAD_DROP_FIELDS.every(
    (field) =>
      GetNormalizedBatchloadEntryValue(drop1[field] as Entry) ===
      GetNormalizedBatchloadEntryValue(drop2[field] as Entry),
  );
}

export function createDropFromRow(exp: BatchLoadExperimentsData): BatchLoadDrop {
  return Object.assign(new BatchLoadDrop(), {
    sample: exp.sample,
    sampleLot: exp.proteinLotId,
    sampleWellRatio: exp.sampleWellRatio,
    protein: exp.protein,
    proteinLot: exp.lot,
    proteinMolecularWeight: exp.mWkDa,
    proteinConcentration: exp.concentration,
    ligand: exp.ligand,
    ligandLot: exp.lot2,
    ligandMolecularWeight: exp.mWkDa2,
    ligandConcentration: exp.concentration2,
    compound: exp.compound,
    compoundLot: exp.lot3,
    compoundMolecularWeight: exp.mWkDa3,
    compoundConcentration: exp.concentration3,
    dropNumber: exp.dropNumber,
    dropVolume: exp.dropVolume,
  });
}

export function groupExperimentsByPlate(
  data: BatchLoadExperimentsData[],
): BatchLoadExperimentsData[] {
  const groups: BatchLoadExperimentsData[] = [];

  data.forEach((exp) => {
    const existing = groups.find((g) => g.plateLocalID.value == exp.plateLocalID.value);

    if (!existing) {
      const tempExp: BatchLoadExperimentsData = cloneDeep(exp);
      tempExp.drops = [];
      tempExp.drops.push(createDropFromRow(exp));
      groups.push(tempExp);
    } else {
      existing.drops.push(createDropFromRow(exp));
    }
  });

  groups.forEach((row, index) => {
    row.experimentNumber.value = (index + 1).toString();
  });

  return groups;
}

function applyNameOption(
  option: BatchloadExperimentReviewExpOptions,
  expName: string,
  templateFormat: string,
  screenName: string,
  temperature: string,
  plateBarcode: string,
  experimentNameTag: string,
  dateToday: string,
): { expName: string; templateFormat: string } {
  switch (option) {
    case BatchloadExperimentReviewExpOptions.SCREENNAME:
      return {
        expName: getHyphenedName(expName, screenName),
        templateFormat: templateFormat + '<ScreenName>',
      };
    case BatchloadExperimentReviewExpOptions.DATE:
      return {
        expName: getHyphenedName(expName, dateToday),
        templateFormat: templateFormat + '<Date>',
      };
    case BatchloadExperimentReviewExpOptions.TEMPERATURE:
      return {
        expName: getHyphenedName(expName, `${temperature}C`),
        templateFormat: templateFormat + '<Temperature>',
      };
    case BatchloadExperimentReviewExpOptions.PLATE_ID:
      return {
        expName: getHyphenedName(expName, '(PlateID)'),
        templateFormat: templateFormat + '<PlateID>',
      };
    case BatchloadExperimentReviewExpOptions.PLATE_BARCODE:
      return {
        expName: getHyphenedName(
          expName,
          plateBarcode == '' || plateBarcode == null || plateBarcode == undefined
            ? '(Barcode)'
            : plateBarcode,
        ),
        templateFormat: templateFormat + '<PlateBarcode>',
      };
    case BatchloadExperimentReviewExpOptions.EXP_NAME_TAG:
      return {
        expName: getHyphenedName(expName, experimentNameTag),
        templateFormat: templateFormat + '<ExperimentNameTag>',
      };
    default:
      return { expName, templateFormat };
  }
}

export function getExperimentName(
  screenName: string,
  temperature: string,
  plateBarcode: string,
  experimentNameTag: string,
  firstCheckboxValue: BatchloadExperimentReviewExpOptions,
  secondCheckboxValue: BatchloadExperimentReviewExpOptions,
  thirdCheckboxValue: BatchloadExperimentReviewExpOptions,
  isSecondCheckboxChecked: boolean,
  isThirdCheckboxChecked: boolean,
  dateToday: string,
): { expName: string; templateFormat: string } {
  let result = applyNameOption(
    firstCheckboxValue,
    '',
    '',
    screenName,
    temperature,
    plateBarcode,
    experimentNameTag,
    dateToday,
  );

  if (isSecondCheckboxChecked && secondCheckboxValue !== firstCheckboxValue) {
    result = applyNameOption(
      secondCheckboxValue,
      result.expName,
      result.templateFormat,
      screenName,
      temperature,
      plateBarcode,
      experimentNameTag,
      dateToday,
    );
  }

  if (
    isThirdCheckboxChecked &&
    thirdCheckboxValue !== firstCheckboxValue &&
    thirdCheckboxValue !== secondCheckboxValue
  ) {
    result = applyNameOption(
      thirdCheckboxValue,
      result.expName,
      result.templateFormat,
      screenName,
      temperature,
      plateBarcode,
      experimentNameTag,
      dateToday,
    );
  }

  return result;
}
